import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, ErrorNotice, Panel } from '@bcecloud/portal-ui';
import { sendJson, errorMessage } from '@/lib/api';

export interface OrgDemoPanelProps {
  readonly organizationId: string;
  readonly organizationName: string;
  readonly isDemo: boolean;
}

/**
 * Marks a client as a demo (Northwind Group): its portal keeps working, but
 * it stays out of internal lists, reports, stats, the assistant and staff
 * alerts.
 *
 * @param props - The organization and whether it is a demo now.
 * @returns The settings section.
 */
export function OrgDemoPanel({ organizationId, organizationName, isDemo }: OrgDemoPanelProps): React.ReactElement {
  const queryClient = useQueryClient();
  const [checked, setChecked] = useState(isDemo);
  const dirty = checked !== isDemo;

  const save = useMutation({
    mutationFn: () => sendJson('patch', `/organizations/${organizationId}`, { isDemo: checked }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['organization', organizationId] }),
        queryClient.invalidateQueries({ queryKey: ['organizations'] }),
      ]);
    },
  });

  return (
    <div className="mt-8">
      <div className="mb-4">
        <h2 className="text-portal-section font-bold tracking-[-0.4px]">Demo client</h2>
        <p className="mt-1 text-portal-small text-portal-muted">
          A demo client is invented, for showing the portal to prospects. Its portal works as any other.
        </p>
      </div>
      <Panel className="max-w-[640px] p-6">
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (dirty && !save.isPending) save.mutate();
          }}
        >
          <div>
            <label htmlFor="organization-demo" className="flex items-center gap-2 text-portal-small">
              <input
                id="organization-demo"
                type="checkbox"
                checked={checked}
                onChange={(event) => setChecked(event.target.checked)}
                className="size-4 accent-brand"
              />
              {organizationName} is a demo client
            </label>
            <p className="mt-1 text-portal-micro text-portal-muted">
              Keeps it out of the Events list, reports, stats and the assistant, and stops its activity from notifying BCE staff. It still
              shows here, with a Demo pill.
            </p>
          </div>

          {save.isError ? <ErrorNotice inline title="Could not save" message={errorMessage(save.error)} /> : null}
          {save.isSuccess && !dirty ? (
            <p role="status" className="text-portal-small text-portal-success">
              Saved.
            </p>
          ) : null}

          <Button type="submit" disabled={!dirty || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </form>
      </Panel>
    </div>
  );
}
